import { ThemeProvider } from "styled-components";
import { darkTheme } from "./global/theme/Themes";
import "./App.css";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import styled from "styled-components";
import { Navbar, Footer } from "./components/shared";
import ScrollManager from "./components/shared/ScrollManager/ScrollManager";
import ConsentBanner from "./analytics/ConsentBanner";
import Home from "./components/pages/Home/Home";
import Stories from "./components/pages/Stories/Stories";
import MartialArts from "./components/pages/MartialArts/MartialArts";
import Products from "./components/pages/Products/Products";
import Projects from "./components/pages/Projects/Projects";
import Achievements from "./components/pages/Achievements/Achievements";
import Workshops from "./components/pages/Workshops/Workshops";
import Dashboard from "./components/pages/Dashboard/Dashboard";
import AiInnovation from "./components/pages/AiInnovation/AiInnovation";
import WorkshopDetail from "./components/pages/Workshops/WorkshopDetail";
import NotFound from "./components/pages/NotFound/NotFound";

/**
 * The page ground. Migrated to the token in D-034, so it follows
 * `<html data-theme>`; the value in dark is byte-identical to the
 * `darkTheme.bg` it replaces (#1c1c27).
 */
const Body = styled.div`
	background-color: var(--bg-base);
	width: 100%;
	overflow-x: hidden;
`;

// Credly profile URL is canonical in src/content/data/profile.json
// (socialLinks[id="credly"]). It is deliberately not repeated here: this
// comment previously carried a slug that had drifted from the JSON.

/**
 * Routes are declared here and nowhere else. Two rules travel with them:
 *
 * - Navbar and Footer sit OUTSIDE <Routes>, so every route carries the same
 *   chrome without re-declaring it per page.
 * - A route ships only when its page is populated (Constitution II.4), and its
 *   navigation link lands in the same commit — the rule the navbar comments
 *   already state for section anchors, extended to routes.
 *
 * Hosting depends on this file, and the dependency changed in D-045. There is
 * no longer a catch-all rewrite sending unmatched paths to /index.html — that
 * is what made /no-such-route answer 200 with the home page. Every route below
 * now needs its own answer in firebase.json:
 *
 *   /         -> build/index.html    (served directly)
 *   /stories  -> build/stories.html  (explicit rewrite, ahead of nothing else)
 *   anything  -> build/404.html      (Firebase's own 404 handling, real 404)
 *
 * So ADDING A ROUTE IS NOW THREE EDITS, not one: this file, `ROUTES` in
 * `scripts/prerender.mjs`, and a rewrite in `firebase.json`. Miss the third
 * and the new route 404s on a hard refresh while working perfectly under
 * client-side navigation — which is to say, perfectly in every test that does
 * not reload the page.
 */
/**
 * The styled-components ThemeProvider is pinned to `darkTheme` on purpose
 * (D-034). It replaces a `useState(true)` whose setter nothing ever called —
 * a light mode that had been one unreachable boolean away since the template
 * era, and an eslint warning ever since.
 *
 * It is NOT wired to the new toggle, and that is the deliberate scope line of
 * D-034's phase 1. Flipping this object would recolour every remaining
 * styled-component at once, against a `lightTheme` palette nobody has
 * reviewed. The token system in `global/theme/tokens.css` is the sanctioned
 * path; components move onto it section by section, and this provider retires
 * when the last one does.
 */
/**
 * Everything inside a router, but not the router itself — split out in D-042 so
 * the same tree can be driven by `BrowserRouter` in the browser and by
 * `StaticRouter` during pre-rendering (`src/entry-server.tsx`).
 *
 * It has to live inside a router rather than wrap one, because `ScrollManager`
 * calls `useLocation` and would throw outside router context. Nothing else about
 * the tree changed: same providers, same order, same chrome, so "/" renders what
 * it rendered before (II.5).
 */
export const AppShell = (): JSX.Element => (
	<ThemeProvider theme={darkTheme}>
		<ScrollManager />
		<Navbar />
		<Body>
			<Routes>
				<Route path="/" element={<Home />} />
				<Route path="/stories" element={<Stories />} />
				{/* Added D-066. A route ships pre-rendered or it does not ship
				    (V.3): this line and the `ROUTES` entry in
				    scripts/prerender.mjs must always land in the same commit,
				    plus a routeMeta entry — `routeMeta()` THROWS on an unknown
				    path, so a route added here and nowhere else fails the build
				    instead of shipping an empty shell with another page's
				    description. */}
				<Route path="/martial-arts" element={<MartialArts />} />
				{/* Added D-067, splitting the old Platforms section in two. Same
				    four-place rule as every route: this line, ROUTES in
				    scripts/prerender.mjs, a routeMeta entry, and the firebase.json
				    rewrite all land together or the route ships an empty shell. */}
				<Route path="/products" element={<Products />} />
				<Route path="/projects" element={<Projects />} />
				{/* Added D-069. Same four-place rule: this line, ROUTES in
				    scripts/prerender.mjs, a routeMeta entry, and the
				    firebase.json rewrite land together or the route ships an
				    empty shell. */}
				<Route path="/achievements" element={<Achievements />} />
				{/* D-076. Pre-rendered in the same commit that declares it — a route
				    here but not in prerender.mjs still WORKS and ships the empty shell,
				    which is the failure Article V.3 exists to prevent, silently. */}
				<Route path="/workshops" element={<Workshops />} />
				{/* D-081 — THE FIRST PARAMETERISED ROUTE ON THIS SITE, and the
				    four-place rule needs one extra reading because of it. This
				    line declares a PATTERN; `ROUTES` in scripts/prerender.mjs
				    and the rewrites in firebase.json list CONCRETE paths, and
				    both derive that list from `workshops.json` rather than
				    repeating it — so a workshop added to the content layer gets
				    a pre-rendered page and a rewrite without anyone editing
				    either file. `routeMeta` generates its entries the same way.
				    What this route does NOT do is fall back: an unknown slug
				    renders the 404 page (see WorkshopDetail), because
				    react-router matches `:slug` against any segment even though
				    Firebase would have served a real 404 on a cold load. */}
				<Route path="/workshops/:slug" element={<WorkshopDetail />} />
				{/* D-094. Same four-place rule as every route above: this line,
				    ROUTES in scripts/prerender.mjs, a routeMeta entry, and the
				    firebase.json rewrite land together, or the route works under
				    client-side navigation and ships an empty shell on a cold load.
				    Both pages are VIEWS OVER EXISTING CONTENT rather than new
				    content: /dashboard draws what the layer already records and
				    /ai-innovation links to where each AI item is documented. Neither
				    introduces a professional fact, which is why neither waits on
				    material under II.4. */}
				<Route path="/dashboard" element={<Dashboard />} />
				<Route path="/ai-innovation" element={<AiInnovation />} />
				{/* Catch-all, and it must stay LAST — react-router v6 ranks by
				    specificity rather than declaration order, so this is
				    belt-and-braces, but the reading order matters to whoever
				    adds the next route. The pre-renderer drives it at "/404" to
				    produce build/404.html (D-045). */}
				<Route path="*" element={<NotFound />} />
			</Routes>
			<Footer />
			{/* OUTSIDE <Routes>, like Navbar and Footer, and for the same reason:
			    a consent choice is site-wide, not per page. It renders null on the
			    server and until an effect runs (D-104) — reading localStorage during
			    the first render would emit one thing into the pre-rendered HTML and
			    another on hydration, and React 18 answers that by re-rendering the
			    whole root (D-042). It is also the component that counts client-side
			    route changes, which is why it sits inside the router. */}
			<ConsentBanner />
		</Body>
	</ThemeProvider>
);

const App = () => (
	<Router>
		<AppShell />
	</Router>
);

export default App;
